import { Launch } from '@carbon/react/icons';
import { NavLink } from 'react-router-dom';
import { getReviewDetails } from 'lib/review-interpretation';
import reviewStatusTypes from 'enums/review_status_types.json';
import type { ReviewAttributes } from 'types/api-responses';
import type { CardInterpretation } from '../types';
import { ReviewStatusIcon, type ReviewGlyphStatus } from './review-status-icon';
import { LeaseBadge } from './document-flags';
import { NOT_AVAILABLE } from './utils';
import styles from './document-card.module.scss';

/**
 * Review-status icon for an interpretation (mirrors Global Interpretations):
 * green check when every section is reviewed, blue in-progress when some are,
 * gray dash when none.
 * @param {object} props The icon.
 * @param {ReviewAttributes | null} [props.review] The interpretation's review.
 * @return {JSX.Element | null} The icon.
 */
function ReviewIcon({ review }: { review?: ReviewAttributes | null }) {
  if (!review) return null;
  const { reviewStatus } = getReviewDetails(review);
  const status: ReviewGlyphStatus =
    reviewStatus === reviewStatusTypes.reviewed
      ? 'reviewed'
      : reviewStatus === reviewStatusTypes.partial
        ? 'partial'
        : 'pending';
  return <ReviewStatusIcon status={status} className={styles.reviewGlyph} />;
}

interface DocumentCardInterpretationsProps {
  // Latest first, mirroring Global Interpretations.
  interpretations: CardInterpretation[];
  count: number;
  onOpen: (interp: CardInterpretation) => void;
}

/**
 * The card's Interpretations column: each interpretation's review status, id
 * link, tear-sheet button and lease badge.
 * @param {DocumentCardInterpretationsProps} props The column.
 * @return {JSX.Element} The column.
 */
export function DocumentCardInterpretations({
  interpretations,
  count,
  onOpen,
}: DocumentCardInterpretationsProps) {
  return (
    <div className={styles.interps}>
      <div className={styles.colHeader}>Interpretations</div>
      {count > 0 ? (
        <div className={styles.interpColList}>
          {interpretations.map((interp) => (
            <div key={interp.id} className={styles.interpRow}>
              <ReviewIcon review={interp.reviewAttributes} />
              {interp.href ? (
                <NavLink
                  to={interp.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.interpLink}
                  onClick={(e) => e.stopPropagation()}
                >
                  {interp.id}
                </NavLink>
              ) : (
                <span>{interp.id}</span>
              )}
              {interp.href && (
                <button
                  type="button"
                  className={styles.interpOpen}
                  aria-label="Open interpretation"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpen(interp);
                  }}
                >
                  <Launch />
                </button>
              )}
              <LeaseBadge status={interp.leaseStatus} />
            </div>
          ))}
        </div>
      ) : (
        <span className={styles.muted}>{NOT_AVAILABLE}</span>
      )}
    </div>
  );
}
